import { useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, Save, Eye, EyeOff, Loader2, Shield, User } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  super_admin: "Super Admin",
  marketing: "Marketing",
  cco: "CCO",
};

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const getInitials = (n) => n?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/auth/profile`, { name, email });
      updateUser(data);
      toast.success("Profile updated");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPEG, PNG or WebP images allowed");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/api/users/${user.id}/upload-profile-pic`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateUser({ profile_pic: data.profile_pic });
      toast.success("Profile picture updated");
    } catch {
      toast.error("Failed to upload picture");
    } finally {
      setUploading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setChangingPw(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to change password");
    } finally {
      setChangingPw(false);
    }
  };

  const profilePicUrl = user?.profile_pic ? `${API.replace('/api', '')}${user.profile_pic}` : null;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto animate-fade-in" data-testid="profile-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">My Profile</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account settings</p>
      </div>

      {/* Profile Info Card */}
      <Card className="border border-gray-200 rounded-xl bg-white mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-6 mb-6">
            <div className="relative group">
              <Avatar className="w-20 h-20 border-2 border-gray-100">
                <AvatarImage src={profilePicUrl} alt={user?.name} />
                <AvatarFallback className="bg-[#0D0D5B] text-white text-xl font-medium">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-testid="upload-pic-btn"
              >
                {uploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePicUpload} className="hidden" data-testid="pic-file-input" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{user?.name}</h2>
              <p className="text-sm text-gray-500">{user?.email}</p>
              <Badge className="mt-1 bg-[#0D0D5B]/10 text-[#0D0D5B] border-[#0D0D5B]/20">
                <Shield className="w-3 h-3 mr-1" /> {roleLabels[user?.role] || user?.role}
              </Badge>
            </div>
          </div>

          <form onSubmit={handleProfileUpdate} className="space-y-4" data-testid="profile-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profile-name">Full Name</Label>
                <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} required className="border-gray-300 rounded-lg" data-testid="profile-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-email">Email</Label>
                <Input id="profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="border-gray-300 rounded-lg" data-testid="profile-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Input value={roleLabels[user?.role] || user?.role} disabled className="bg-gray-50 border-gray-200 rounded-lg" />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={saving} className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="save-profile-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card className="border border-gray-200 rounded-xl bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" /> Change Password
          </h3>
          <form onSubmit={handlePasswordChange} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="current-pw">Current Password</Label>
              <div className="relative">
                <Input id="current-pw" type={showPasswords ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="border-gray-300 rounded-lg pr-10" data-testid="current-password-input" />
                <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-pw">New Password</Label>
                <Input id="new-pw" type={showPasswords ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="border-gray-300 rounded-lg" data-testid="new-pw-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pw">Confirm New Password</Label>
                <Input id="confirm-pw" type={showPasswords ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="border-gray-300 rounded-lg" data-testid="confirm-pw-input" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={changingPw} variant="outline" className="border-[#0D0D5B] text-[#0D0D5B] hover:bg-[#0D0D5B]/5 rounded-lg" data-testid="change-pw-btn">
                {changingPw ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Change Password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
